import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, takeUntil } from 'rxjs';
import { ITaskDialogData, TaskDialogComponent } from 'src/app/modals/task-dialog/task-dialog.component';
import { TaskGroupDialog } from 'src/app/modals/task-group-dialog/task-group-dialog.component';
import { TaskGroupAssignmentDialog } from 'src/app/modals/task-group-assignment-dialog/task-group-assignment-dialog.component';
import { Task } from 'src/app/models/task.model';
import { TaskGroup } from 'src/app/models/taskGroup.model';
import { TaskGroupService } from 'src/app/services/taskGroup.service';
import { TaskService } from 'src/app/services/task.service';
import { ToastService } from 'src/app/services/toast.service';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FLYOUT_DATA } from 'src/app/shared/flyout/flyout-container/flyout-data.token';
import { IFormDialogData } from 'src/app/shared/form/form.component';
import nameOf from 'src/app/shared/form/nameOf.helper';

@Component({
  standalone: false,
    selector: 'app-task-group-details-popup',
    templateUrl: './task-group-details-popup.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './task-group-details-popup.component.scss',
})
export class TaskGroupDetailsPopupComponent implements OnInit, OnDestroy {
    taskGroup: TaskGroup | null = null;
    isLoading = false;
    isTasksLoading = false;
    tasksDataSource = new MatTableDataSource<Task>();
    readonly tasksColumns = [nameOf<Task>(x => x.name), 'actions'];
    private readonly destroy$ = new Subject<void>();
    private taskGroupId: string = '';

    constructor(
        @Inject(FLYOUT_DATA) public data: string | TaskGroup,
        private taskGroupService: TaskGroupService,
        private taskService: TaskService,
        private toastService: ToastService,
        protected dialog: MatDialog,
    ) {
        // Extract ID from either a TaskGroup object or just an ID
        if (typeof data === 'object' && data.id) {
            this.taskGroupId = data.id;
        } else if (typeof data === 'string') {
            this.taskGroupId = data;
        }
    }

    ngOnInit(): void {
        if (this.data) {
            if (typeof this.data === 'object' && this.data.id) {
                // Received full TaskGroup object from grid
                this.taskGroup = this.data;
                this.tasksDataSource.data = this.data.aptTasks ?? [];
                // Load any fresh data if aptTasks is missing
                if (!this.data.aptTasks || this.data.aptTasks.length === 0) {
                    this.loadTasks();
                }
            } else {
                // Received just an ID
                this.loadDetails();
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    loadDetails(): void {
        this.isLoading = true;
        this.taskGroupService.getById(this.taskGroupId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (group) => {
                this.taskGroup = group;
                this.isLoading = false;
                this.loadTasks();
            },
            error: (error) => {
                this.toastService.showError(error);
                this.isLoading = false;
            },
        });
    }

    loadTasks(): void {
        this.isTasksLoading = true;
        this.taskService
            .get('', 'name', 'asc', 0, 200, JSON.stringify({ taskGroupId: this.taskGroupId }))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.tasksDataSource.data = response.data ?? [];
                    this.isTasksLoading = false;
                },
                error: (error) => {
                    this.toastService.showError(error);
                    this.isTasksLoading = false;
                },
            });
    }

    openEditGroupDialog(): void {
        const model: IFormDialogData = { id: this.taskGroupId };
        const ref = this.dialog.open(TaskGroupDialog, { data: model, width: '600px', disableClose: true });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                this.loadDetails();
            }
        });
    }

    openAssignDialog(): void {
        const model: IFormDialogData = { id: this.taskGroupId };
        const ref = this.dialog.open(TaskGroupAssignmentDialog, { data: model, width: '600px', disableClose: true });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                this.loadDetails();
            }
        });
    }

    openCreateTaskDialog(): void {
        const data: ITaskDialogData = { id: '', groupId: this.taskGroupId };
        const ref = this.dialog.open(TaskDialogComponent, { data, width: '600px', disableClose: true });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                this.loadTasks();
            }
        });
    }

    openEditTaskDialog(taskId: string): void {
        const data: ITaskDialogData = { id: taskId, groupId: this.taskGroupId };
        const ref = this.dialog.open(TaskDialogComponent, { data, width: '600px', disableClose: true });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                this.loadTasks();
            }
        });
    }

    openDeleteTaskDialog(task: Task): void {
        const ref = this.dialog.open(DeleteDialog, {
            data: { id: task.id, service: this.taskService },
            width: '440px',
            disableClose: true,
        });
        ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result) {
                this.loadTasks();
            }
        });
    }
}
